import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { StrongText } from "@/components/strong-text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type AdminUserSummary, useMe, useRevokeUserSessions } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";

import { locksAccount, roleLock, roleLockText } from "./utils";

interface Props {
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Sign out everywhere": ends every active session of a user.
 * Not offered for your own account, nor for an account above the signed-in
 * person's role (an owner's for an admin, a developer's for anyone but a
 * developer): the dialog then says why and offers only Close. The backend
 * refuses both too.
 */
export function UserRevokeSessionsDialog({ user, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const revokeMutation = useRevokeUserSessions();
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const isSelf = !!me && !!user && me.email.toLowerCase() === user.email.toLowerCase();
  const lock = user ? roleLock(me, user) : null;
  const locked = !isSelf && locksAccount(lock);
  const blocked = isSelf || locked;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setFailure(null);
    }
    onOpenChange(next);
  };

  const handleRevoke = async () => {
    if (!user) {
      return;
    }
    setFailure(null);
    try {
      await revokeMutation.mutateAsync({ userId: user.id });
      handleOpenChange(false);
    } catch (err) {
      setFailure(inlineFailure({ error: err }));
    }
  };
  const error = failure && apiErrorMessage(failure.error, "revokeSessions");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("users.signOutEverywhere")}</DialogTitle>
          <DialogDescription>
            {isSelf ? (
              t("users.revokeSelf")
            ) : locked ? (
              roleLockText(lock)
            ) : (
              <StrongText i18nKey="users.revokeBody" values={{ email: user?.email ?? "" }} />
            )}
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {blocked ? t("common.close") : t("common.cancel")}
          </Button>
          {!blocked && (
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("users.signOutEverywhere")
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
